import { useQuery } from "@apollo/client";
import { Alert } from "@mui/material";
import { ReactElement } from "react";

import {
  ActBlueWebhookIntegration,
  AllIntegrationTypes,
  GetSubaccountShortcodeQuery,
  GetSubaccountShortcodeQueryVariables,
  IntegrationServiceType,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { GET_SUBACCOUNT_SHORTCODE } from "~frontend/graphql/shortcode";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useCurrentUserIsStaff } from "~frontend/providers/LoggedInUserProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";

import { ActBlueIntegrations } from "./ActBlueIntegrations";
import FixedKeywords from "./FixedKeywords";
import OptInKeywords from "./OptInKeywords";
import Subscribers from "./Subscribers";

export default function Shortcode(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const isStaff = useCurrentUserIsStaff();

  useBreadcrumbs([{ label: "Shortcode", path: subaccountLink("/shortcode") }]);

  const { data, loading, error, startPolling, stopPolling } = useQuery<
    GetSubaccountShortcodeQuery,
    GetSubaccountShortcodeQueryVariables
  >(GET_SUBACCOUNT_SHORTCODE, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  return (
    <PageContainer>
      <AsyncWrapper loading={loading} errors={[error]} data={data}>
        {data => {
          if (!data.getSubaccount.shortcode) {
            return <ErrorCard errors={["No shortcode found"]} />;
          }

          if (!data.getSubaccount.shortcode.shortCodeConfig) {
            return (
              <ErrorCard errors={["Shortcode is missing configuration"]} />
            );
          }

          return (
            <FlexBox flexDirection="column" gap={6}>
              {data.getSubaccount.shortcode.state === "NEW" && isStaff && (
                <Alert severity="warning">
                  This shortcode is NOT active, and this page is not visible to
                  the client.
                </Alert>
              )}

              <FixedKeywords
                shortcodeConfig={data.getSubaccount.shortcode.shortCodeConfig}
              />
              <OptInKeywords shortcodeId={data.getSubaccount.shortcode.id} />
              <ActBlueIntegrations
                integrations={
                  (
                    (data.getSubaccount.integrations ||
                      []) as AllIntegrationTypes[]
                  ).filter(
                    integration =>
                      integration.service ===
                        IntegrationServiceType.ActBlueWebhook &&
                      (integration as ActBlueWebhookIntegration).settings
                        ?.subscribeDonors
                  ) as ActBlueWebhookIntegration[]
                }
              />
              <Subscribers
                shortcode={data.getSubaccount.shortcode}
                startShortcodePolling={startPolling}
                stopShortcodePolling={stopPolling}
              />
            </FlexBox>
          );
        }}
      </AsyncWrapper>
    </PageContainer>
  );
}
